'use client';

import {useState, useCallback, useMemo} from 'react';
import {useCommonData} from '@udemy-v2/common-data';
import {useFeatures} from '@udemy-v2/react-experimentation';
import {udExpiringLocalStorage} from '@udemy-v2/utils';
import {NVD_PASSWORDLESS_LOGIN_EXPERIMENT} from '../constants';

// The modal only shows once per user across page loads
const browserStore = udExpiringLocalStorage(
    'frontends-course-landing-page',
    'activation-offer-modal',
);

const STORAGE_KEY = 'udemy/activation-offer-modal/shown';

/**
 * Hook to manage activation offer modal state and visibility logic
 */
export function useActivationOfferModal() {
    const commonData = useCommonData();
    const {isLoading, featureVariantAssignments} = useFeatures([NVD_PASSWORDLESS_LOGIN_EXPERIMENT]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Check if user is logged out
    const isLoggedOut = useMemo(() => !commonData?.user?.id, [commonData?.user?.id]);

    // Check if experiment is enabled and enable_nvd flag is true
    const isExperimentEnabled = useMemo(() => {
        if (isLoading) return false;

        const variant = featureVariantAssignments.get(NVD_PASSWORDLESS_LOGIN_EXPERIMENT);
        const config = variant?.configuration as {enable_nvd?: boolean} | undefined;
        return !!(config?.enable_nvd === true);
    }, [featureVariantAssignments, isLoading]);

    // Check if modal was already shown (using udExpiringLocalStorage)
    const hasBeenShown = useMemo(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        return !!browserStore.get(STORAGE_KEY);
    }, []);

    const canShowModal = useMemo(() => {
        return isLoggedOut && isExperimentEnabled && !hasBeenShown;
    }, [isLoggedOut, isExperimentEnabled, hasBeenShown]);

    // Show modal and mark as shown in udExpiringLocalStorage
    const showModal = useCallback(() => {
        if (canShowModal) {
            setIsModalOpen(true);
            if (typeof window !== 'undefined') {
                browserStore.set(STORAGE_KEY, true);
            }
        }
    }, [canShowModal]);

    const hideModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    return {
        isOpen: isModalOpen,
        showModal,
        hideModal,
        canShowModal,
    };
}
